"use client";

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import Link from "next/link";
import { PORTAL_URL } from "@/app/docs/portal";
import { ExternalLinkIcon, LockIcon, XIcon } from "@/app/docs/docs-icons";
import { SearchIcon } from "@/components/feature-icons";

type NavItem = { label: string; href: string; external?: boolean };

const SIDEBAR: { title: string; items: NavItem[] }[] = [
  {
    title: "Overview",
    items: [
      { label: "Introduction", href: "#introduction" },
      { label: "Getting Started", href: "#getting-started" },
      { label: "Authentication", href: "#authentication" },
    ],
  },
  {
    title: "Capabilities",
    items: [
      { label: "People", href: "#people" },
      { label: "Addresses", href: "#addresses" },
      { label: "Companies", href: "#companies" },
      { label: "Property", href: "#property" },
      { label: "Contact", href: "#contact" },
      { label: "Identity & Screening", href: "#identity-screening" },
      { label: "Bulk & Enrichment", href: "#bulk-enrichment" },
      { label: "Geospatial", href: "#geospatial" },
    ],
  },
  {
    title: "Guides",
    items: [
      { label: "CRM Integration", href: "#integration-patterns" },
      { label: "Casework Integration", href: "#integration-patterns" },
      { label: "Bulk Workflows", href: "#bulk" },
      { label: "Search & Matching", href: "#search-matching" },
      { label: "Security", href: "#security" },
    ],
  },
  {
    title: "Resources",
    items: [
      { label: "Developer Support", href: "#developer-support" },
      { label: "Pricing", href: "/pricing" },
      { label: "API Portal", href: PORTAL_URL, external: true },
    ],
  },
];

const TOC: { id: string; label: string }[] = [
  { id: "getting-started", label: "Getting Started" },
  { id: "authentication", label: "Authentication" },
  { id: "capabilities", label: "Capabilities" },
  { id: "integration-patterns", label: "Integration Patterns" },
  { id: "openapi", label: "OpenAPI" },
  { id: "secret-keys", label: "Secret Keys" },
  { id: "permissions", label: "Permissions" },
  { id: "kyc-aml", label: "KYC & AML" },
  { id: "bulk", label: "Bulk" },
  { id: "search-matching", label: "Search & Matching" },
  { id: "errors", label: "Errors" },
  { id: "security", label: "Security" },
  { id: "developer-support", label: "Developer Support" },
];

const CAPABILITY_IDS = [
  "people",
  "addresses",
  "companies",
  "property",
  "contact",
  "identity-screening",
  "bulk-enrichment",
  "geospatial",
];

const WATCH_IDS = [
  "introduction",
  "access",
  "getting-started",
  "authentication",
  "request-structure",
  "capabilities",
  ...CAPABILITY_IDS,
  "integration-patterns",
  "api-access-included",
  "openapi",
  "secret-keys",
  "permissions",
  "kyc-aml",
  "bulk",
  "search-matching",
  "errors",
  "security",
  "developer-support",
];

const SEARCH_INDEX: { label: string; id: string }[] = [
  { label: "Introduction", id: "introduction" },
  { label: "Public and customer access", id: "access" },
  { label: "Getting started", id: "getting-started" },
  { label: "Authentication", id: "authentication" },
  { label: "Request structure", id: "request-structure" },
  { label: "API capabilities", id: "capabilities" },
  { label: "People Intelligence", id: "people" },
  { label: "Address Intelligence", id: "addresses" },
  { label: "Company Intelligence", id: "companies" },
  { label: "Property Intelligence", id: "property" },
  { label: "Contact Intelligence", id: "contact" },
  { label: "Identity & Screening", id: "identity-screening" },
  { label: "Bulk & Enrichment", id: "bulk-enrichment" },
  { label: "Location & Geospatial", id: "geospatial" },
  { label: "Integration patterns", id: "integration-patterns" },
  { label: "API access is included", id: "api-access-included" },
  { label: "OpenAPI reference", id: "openapi" },
  { label: "Application-specific API keys", id: "secret-keys" },
  { label: "Permissions", id: "permissions" },
  { label: "KYC & AML through the API", id: "kyc-aml" },
  { label: "Bulk workflows", id: "bulk" },
  { label: "Understanding search results", id: "search-matching" },
  { label: "Error handling", id: "errors" },
  { label: "Security & governance", id: "security" },
  { label: "Developer support", id: "developer-support" },
];

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  window.history.replaceState(null, "", `#${id}`);
}

function SidebarNav({
  activeId,
  onNavigate,
}: {
  activeId: string;
  onNavigate?: () => void;
}) {
  function onHash(e: MouseEvent<HTMLAnchorElement>, href: string) {
    e.preventDefault();
    onNavigate?.();
    scrollToId(href.slice(1));
  }

  return (
    <div className="space-y-6">
      {SIDEBAR.map((group) => {
        const firstActive = group.items.findIndex(
          (i) => i.href === `#${activeId}`,
        );
        return (
          <div key={group.title}>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-on-surface-variant">
              {group.title}
            </p>
            <ul className="space-y-px border-l border-outline-variant/60">
              {group.items.map((item, i) => {
                const active = i === firstActive;
                const cls = `-ml-px flex items-center gap-1.5 border-l-2 py-1 pl-3 text-[13px] transition-colors ${
                  active
                    ? "border-brand-cyan bg-brand-cyan/[0.07] font-medium text-brand-navy"
                    : "border-transparent text-on-surface-variant hover:border-outline-variant hover:text-brand-navy"
                }`;
                if (item.external) {
                  return (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cls}
                      >
                        {item.label}
                        <ExternalLinkIcon className="h-3 w-3 shrink-0" />
                      </a>
                    </li>
                  );
                }
                if (item.href.startsWith("/")) {
                  return (
                    <li key={item.label}>
                      <Link href={item.href} className={cls} onClick={onNavigate}>
                        {item.label}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className={cls}
                      onClick={(e) => onHash(e, item.href)}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      <a
        href={PORTAL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-md border border-outline-variant bg-[#f7f9fc] p-3 transition-colors hover:border-brand-electric"
      >
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-brand-navy">
          Full API Reference
          <ExternalLinkIcon className="h-3 w-3 shrink-0" />
        </span>
        <span className="mt-1 flex items-center gap-1 text-[11px] text-on-surface-variant">
          <LockIcon className="h-3 w-3 shrink-0" />
          Portal sign-in required
        </span>
      </a>
    </div>
  );
}

export function DocsShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [query, setQuery] = useState("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const els = WATCH_IDS.map((id) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (els.length === 0) return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        for (const id of WATCH_IDS) {
          if (visible.has(id)) {
            setActiveId(id);
            break;
          }
        }
      },
      { rootMargin: "-134px 0px -68% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = "";
    };
  }, [drawerOpen]);

  const q = query.trim().toLowerCase();
  const results = q
    ? SEARCH_INDEX.filter((x) => x.label.toLowerCase().includes(q)).slice(0, 8)
    : [];
  const tocActive = CAPABILITY_IDS.includes(activeId) ? "capabilities" : activeId;

  return (
    <>
      {/* Docs sub-header */}
      <div className="sticky top-20 z-30 border-b border-outline-variant/70 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-11 max-w-[1320px] items-center gap-3 px-4 sm:px-6 lg:px-8">
          <span className="shrink-0 text-sm font-semibold text-brand-navy">
            KleipData API Docs
          </span>

          <div className="relative ml-auto w-full max-w-[16rem]">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search documentation..."
              aria-label="Search API documentation"
              className="h-8 w-full rounded-md border border-outline-variant bg-[#f7f9fc] pl-8 pr-3 text-[13px] text-brand-navy outline-none placeholder:text-on-surface-variant/70 focus:border-brand-electric focus:ring-2 focus:ring-brand-electric/20"
            />
            {results.length > 0 && (
              <ul className="absolute inset-x-0 top-[calc(100%+4px)] z-40 max-h-72 overflow-y-auto rounded-md border border-outline-variant bg-white py-1 shadow-lg">
                {results.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setQuery("");
                        scrollToId(r.id);
                      }}
                      className="block w-full px-3 py-1.5 text-left text-[13px] text-brand-navy hover:bg-[#f7f9fc]"
                    >
                      {r.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden shrink-0 items-center gap-1 text-[13px] font-semibold text-brand-electric hover:underline sm:inline-flex"
          >
            API Portal
            <ExternalLinkIcon className="h-3.5 w-3.5" />
          </a>

          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="shrink-0 rounded-md border border-outline-variant px-2.5 py-1 text-[13px] font-medium text-brand-navy transition-colors hover:bg-[#f7f9fc] lg:hidden"
          >
            Browse Docs
          </button>
        </div>
      </div>

      {/* 3-column layout */}
      <div className="mx-auto grid max-w-[1320px] px-4 sm:px-6 lg:grid-cols-[236px_minmax(0,1fr)] lg:gap-10 lg:px-8 xl:grid-cols-[236px_minmax(0,1fr)_208px]">
        <aside className="hidden lg:block">
          <nav
            aria-label="Documentation"
            className="sticky top-[7.75rem] max-h-[calc(100vh-8.75rem)] overflow-y-auto py-10 pr-1"
          >
            <SidebarNav activeId={activeId} />
          </nav>
        </aside>

        <main className="min-w-0 max-w-[800px] py-10 lg:py-12">{children}</main>

        <aside className="hidden xl:block">
          <nav
            aria-label="On this page"
            className="sticky top-[7.75rem] max-h-[calc(100vh-8.75rem)] overflow-y-auto py-12"
          >
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.1em] text-on-surface-variant">
              On this page
            </p>
            <ul className="space-y-px border-l border-outline-variant/70">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToId(t.id);
                    }}
                    aria-current={tocActive === t.id ? "true" : undefined}
                    className={`-ml-px block border-l-2 py-1 pl-3 text-[13px] transition-colors ${
                      tocActive === t.id
                        ? "border-brand-electric font-medium text-brand-electric"
                        : "border-transparent text-on-surface-variant hover:text-brand-navy"
                    }`}
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-[60] lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Documentation navigation"
        >
          <div
            className="absolute inset-0 bg-brand-navy/40"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 flex w-[300px] max-w-[85vw] flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-outline-variant/70 px-4 py-3">
              <span className="text-sm font-semibold text-brand-navy">
                Documentation
              </span>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation"
                className="rounded p-1 text-on-surface-variant transition-colors hover:bg-[#f7f9fc]"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4">
              <SidebarNav
                activeId={activeId}
                onNavigate={() => setDrawerOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
